import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Search, Edit, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { telegramAdminService } from '@/services/telegram-admin-service';
import { AddAdminUserDialog } from './dialogs/AddAdminUserDialog';
import { EditAdminUserDialog } from './dialogs/EditAdminUserDialog';

export function TelegramAdminUserManagement() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['telegram-admins', page, search, statusFilter],
    queryFn: () =>
      telegramAdminService.getAdminUsers(page, 10, {
        search,
        status: statusFilter === 'all' ? undefined : statusFilter === 'active',
      }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive
        ? telegramAdminService.deactivateAdmin(id)
        : telegramAdminService.activateAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram-admins'] });
      toast.success('Admin status updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const totalPages = data?.count ? Math.ceil(data.count / 10) : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>👥 Registered Admin Users</CardTitle>
          <CardDescription>
            Manage Telegram admin users who can interact with the bot
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-center flex-wrap">
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Admin
            </Button>

            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg">
            {isLoading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Telegram ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notifications</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.data?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No admin users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.data?.map((admin: any) => (
                      <TableRow key={admin.id}>
                        <TableCell className="font-medium">
                          {admin.telegram_first_name}{' '}
                          {admin.telegram_last_name || ''}
                        </TableCell>
                        <TableCell>@{admin.telegram_username || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {admin.telegram_id}
                        </TableCell>
                        <TableCell>
                          {admin.is_active ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {admin.notification_enabled ? (
                            <Badge variant="outline">Enabled</Badge>
                          ) : (
                            <Badge variant="secondary">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {admin.last_active_at
                            ? new Date(admin.last_active_at).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingAdmin(admin)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant={admin.is_active ? 'destructive' : 'default'}
                            onClick={() =>
                              toggleActiveMutation.mutate({
                                id: admin.id,
                                isActive: admin.is_active,
                              })
                            }
                            disabled={toggleActiveMutation.isPending}
                          >
                            {admin.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddAdminUserDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => {
          setShowAddDialog(false);
          queryClient.invalidateQueries({ queryKey: ['telegram-admins'] });
        }}
      />

      <EditAdminUserDialog
        open={!!editingAdmin}
        onOpenChange={(open) => !open && setEditingAdmin(null)}
        admin={editingAdmin}
        onSuccess={() => {
          setEditingAdmin(null);
          queryClient.invalidateQueries({ queryKey: ['telegram-admins'] });
        }}
      />
    </div>
  );
}
